import { AnyAction, configureStore, type Middleware } from '@reduxjs/toolkit';
import { storyCopierSliceReducer } from './reducers/StoryCopierSlice';
import { linkModalSliceReducer } from './reducers/LinkModalSlice';
import { linkTipSliceReducer } from './reducers/LinkTipSlice';
import { digestReducer } from './reducers/DigestReducer';
import { downTimeSliceReducer } from './reducers/DowntimeSlice';
import { modalReducer } from './reducers/ModalReducer';
import { storyWithHistoryReducer } from './reducers/StoryWithHistoryReducer';
import { autosaveMiddleware } from './middleware/autosaveMiddleware';
import { personalizedStoryMiddleware } from './middleware/personalizedStoryMiddleware';
import { personalizedStoryReducer } from './reducers/PersonalizedStoryReducer';
import { storySaveStatusReducer } from './reducers/StorySaveStatusReducer';
import { actionComponentReducer } from './reducers/ActionComponentReducer';
import { accessManagerReducer } from './reducers/AccessManagerReducer';
import { shareAndEmbedReducer } from './reducers/ShareAndEmbedReducer';
import { windowSizeReducer } from './reducers/WindowSizeReducer';
import { userSessionReducer } from './reducers/UserSessionReducer';
import { coreSavingReducer } from './reducers/CoreSavingReducer';
import { dropHintReducer } from './reducers/DropHintReducer';
import { userSessionMiddleware } from './middleware/userSessionMiddleware';
import { storyChangesReducer } from './reducers/StoryChangesReducer';
import type { StorytellerState } from './StorytellerReduxState';
import { pageLoadingReducer } from './LoadingReduxState';
import { assetSelectorReducer } from './reducers/AssetSelectorReducer';
import { richTextEditorReducer } from './reducers/RichTextEditorReducer';
import { gridFilterReducer } from './reducers/GridFilterReducer';
import StorytellerUtils from '../lib/StorytellerUtils';
import * as selectors from './selectors';

/**
 * import reducers and slices here
 * @deprecated use `StorytellerReduxStoreFactory()` instead.
 */
export const StorytellerReduxStore = configureStore<
  StorytellerState,
  AnyAction,
  Middleware<StorytellerState>[]
>({
  reducer: {
    accessManager: accessManagerReducer,
    actionComponentReducer,
    assetSelector: assetSelectorReducer,
    digest: digestReducer,
    downtimeSlice: downTimeSliceReducer,
    dropHint: dropHintReducer,
    gridFilter: gridFilterReducer,
    storyWithHistory: storyWithHistoryReducer,
    linkModalSlice: linkModalSliceReducer,
    linkTipSlice: linkTipSliceReducer,
    storyCopierSlice: storyCopierSliceReducer,
    storySaveStatus: storySaveStatusReducer,
    modals: modalReducer,
    personalizedStories: personalizedStoryReducer,
    richTextEditor: richTextEditorReducer,
    shareAndEmbedReducer,
    userSession: userSessionReducer,
    windowSize: windowSizeReducer,
    coreSaving: coreSavingReducer,
    storyChanges: storyChangesReducer,
    pageLoading: pageLoadingReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).prepend(autosaveMiddleware, personalizedStoryMiddleware, userSessionMiddleware),
});

export function StorytellerReduxStoreFactory() {
  return StorytellerReduxStore;
}

export type StorytellerDispatch = typeof StorytellerReduxStore.dispatch;

// Export Redux store to window for Cheetah tests and external components
StorytellerUtils.export(StorytellerReduxStore, 'storyteller.reduxStore');

// Export selectors to window for Cheetah tests
StorytellerUtils.export(selectors, 'storyteller.selectors');
